import { RETAIL_SIGNALS, isEmptyPayload, normalizeEnvelope } from '../../lib/agent/envelope';
import { formatApiResult } from '../../lib/agent/apiResult';
import { DECLARED_ACTIONS } from '../../lib/agent/discovery';
import {
  resolveScreen, pathFor, screenForPath, paramsFor, seatAllows,
} from '../../lib/agent/screens';
import { AGENT_FORMS, AGENT_SIGNALS } from '../../state/agentSlice';
import { agentRuntime } from './runtime';
import { agentNavigate, currentAgentLocation } from './navigation';
import { setChatOpen } from '../../state/uiSlice';

import { startAgentSignal } from './requests';
import { debug } from '../../lib/debug';
/** The dock opens when the agent moves the reader, so its answer stays in view on the new screen. */
function keepAssistantInView() {
  try {
    const { store } = agentRuntime();
    if (store && !store.getState()?.ui?.chatOpen) store.dispatch(setChatOpen(true));
  } catch (err) {
    debug('agent → could not open the dock', err?.message || err);
  }
}

/**
 * Carry out one agent envelope, the Ki_UI way: navigate, fill forms, fire signals.
 *
 * The provider has already shown the reply and handled any handoff; this is the
 * part that changes the app. Nothing the agent names is dispatched raw. A form
 * update reaches the store only when the catalogue declares it
 * (`DECLARED_ACTIONS`), where the `agent` slice keeps only that form's fields;
 * anything else is one of the few legacy commands below, or logged and
 * ignored — Ki_UI's "dispatch any string with the leftover fields as payload"
 * leaked `agent_action`, `language` and `call_back_navigation` into reducers.
 *
 * Each command runs in its own try/catch, so a bug handling one cannot swallow
 * the rest of the envelope, and the agent hears about it once
 * (`<type> failed: client error — <msg>`) instead of never.
 */

/** A links card lists at most this many rows; the rest are noise in a chat column. */
const MAX_LINKS = 12;

/** Retail vocabulary the web agents still send. They get one honest refusal per envelope. */
const RETAIL_REFUSAL = 'not available on the PMS desk';

/** The legacy `UPDATE_*` commands the app still acts on (as link cards). */
const LINK_CARD_TYPES = ['UPDATE_SUGGESTION_DATA', 'UPDATE_SCREEN_BUILDER_RESULT'];

/** The signal the retail prompts share with the desk; see `retailFormIn`. */
const RUN_ANALYSIS = 'REQUEST_RUN_ANALYSIS';

const isObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const str = (v) => (v == null ? '' : String(v).trim());
const first = (...values) => values.map(str).find(Boolean) || '';

/** The seat links and navigations are checked against. */
function currentSeat() {
  const ui = agentRuntime().store.getState()?.ui;
  return ui?.assignedRole || ui?.role || null;
}

/** A catalogue URL for a detail screen, or null when the seat cannot open it or the id is unusable. */
function linkFor(key, params) {
  const seat = currentSeat();
  if (!seat || !seatAllows(seat, key)) return null;
  const built = pathFor(key, params);
  if (!built || !built.ok) return null;
  const search = String(built.search || '').replace(/^\?/, '');
  return search ? `${built.pathname}?${search}` : built.pathname;
}

function addLinksCard(title, items) {
  if (!items.length) return false;
  agentRuntime().addMessage({ from: 'sys', kind: 'links', card: { title, items: items.slice(0, MAX_LINKS) } });
  return true;
}

/* ── legacy commands ────────────────────────────────────────────────────── */

/** The screen handle in a payload, in either spelling the agent uses. */
const screenHandle = (payload) => first(payload.url_id, payload.urlId);

/**
 * `UPDATE_SUGGESTION_DATA` — the agent's shortlist, as links the user can open.
 * Rows the seat cannot open are left out rather than linked to a guard bounce.
 */
function suggestionLinks(payload) {
  const items = [];
  const seen = new Set();
  const push = (item) => {
    if (!item || seen.has(item.to)) return;
    seen.add(item.to);
    items.push(item);
  };
  const list = (v) => (Array.isArray(v) ? v : []);

  list(payload.suggestedMutualFunds).forEach((f) => {
    const symbol = typeof f === 'string' ? str(f) : first(f?.securitySymbol, f?.symbol);
    if (!symbol) return;
    const to = linkFor('fund', { id: symbol });
    const name = typeof f === 'string' ? '' : first(f?.name, f?.schemeName, f?.fundName, f?.securityName);
    if (to) push({ label: name || symbol, sub: name ? `Mutual fund · ${symbol}` : 'Mutual fund', to });
  });
  list(payload.suggestedStocks).forEach((s) => {
    const symbol = typeof s === 'string' ? str(s) : first(s?.securitySymbol, s?.symbol);
    if (!symbol) return;
    const to = linkFor('stock', { symbol });
    const name = typeof s === 'string' ? '' : first(s?.name, s?.companyName, s?.securityName);
    if (to) push({ label: name || symbol, sub: name ? `Stock · ${symbol}` : 'Stock', to });
  });
  /* `suggestedModels` is the Ki_UI spelling of the same list. */
  [...list(payload.suggestedAssetModels), ...list(payload.suggestedModels)].forEach((m) => {
    const id = typeof m === 'string' ? str(m) : first(m?.id, m?.modelId, m?.assetModelId);
    if (!id) return;
    const to = linkFor('model', { id });
    const name = typeof m === 'string' ? '' : first(m?.name, m?.modelName, m?.title);
    if (to) push({ label: name || `Model ${id}`, sub: 'Model portfolio', to });
  });

  /* A stock list stored under a handle (screen_stocks, get_stock_alpha_ideas): the
     card names a few; the Screener lists every match with its figures. */
  const handle = screenHandle(payload);
  const kind = first(payload.screenType, payload.screen_type);
  if (handle && !/^\d+$/.test(handle) && /stock/i.test(kind || '') && list(payload.suggestedStocks).length) {
    const to = linkFor('deskIdeas', { tab: 'screener', screen: handle });
    const count = Number(first(payload.count)) || null;
    if (to) push({ label: `See all${count ? ` ${count}` : ''} →`, sub: 'Every match, with its figures, on the Screener', to });
  }

  const kinds = new Set(items.filter((i) => !/^See all/.test(i.label)).map((i) => i.sub.split(' ·')[0]));
  const title = kinds.size === 1 && kinds.has('Mutual fund') ? 'Suggested funds'
    : kinds.size === 1 && kinds.has('Stock') ? 'Suggested stocks'
      : kinds.size === 1 && kinds.has('Model portfolio') ? 'Suggested models'
        : 'Suggestions';
  if (!addLinksCard(title, items)) debug('agent → suggestions had nothing linkable', payload);
}

/**
 * Whether a screen payload is one FundResearch can show.
 *
 * Only a scheme screen is: the screen is read back by symbol out of the fund
 * stores, so a stock or model screen opens an empty table there. An unstated
 * type is taken as a scheme, which is what the fund screener sends.
 *
 * Both the navigation ([`fundScreenId`]) and the link card ask this, and they
 * have to agree — a card offering what the navigation refused sends the reader
 * to a blank page by hand.
 */
const isFundScreen = (payload) => {
  const kind = first(payload.screenType, payload.screen_type);
  return !kind || /mutual|fund|mf/i.test(kind);
};


/**
 * `UPDATE_SCREEN_BUILDER_RESULT` — a screen was built; point at the research
 * screen that lists funds.
 *
 * Only a fund screen is offered, for the reason `isFundScreen` gives: a stock
 * or model screen linked here opened an empty table, while `fundScreenId` had
 * already refused it for the navigation.
 */
function screenBuilderLink(payload) {
  if (!isFundScreen(payload)) {
    debug('agent → screen builder result skipped (not a fund screen)', payload);
    return;
  }
  const id = screenHandle(payload);
  const to = linkFor('research', id ? { url_id: id } : {});
  if (!to) {
    debug('agent → screen builder result not linkable for this seat', payload);
    return;
  }
  const kind = first(payload.screenType, payload.screen_type);
  addLinksCard('Screen results', [{
    label: kind ? `Open ${kind.replace(/[_-]+/g, ' ')} results` : 'Open the screen results',
    sub: id ? `Screen ${id}` : undefined,
    to,
  }]);
}

/**
 * `SET_SELECTED_GOAL` / `SET_AUTO_SELECT_GOAL_ID` — retail goal selection.
 *
 * On the desk a goal belongs to a client, and its page needs both ids. With
 * both known it opens; otherwise it is ignored. Silent either way: these arrive
 * as side effects of retail flows whose agent never expected a screen change,
 * and a failure report would start a spoken turn about something the user did
 * not ask for.
 */
async function selectGoal(type, payload) {
  if (payload.dummy === true) return false;
  const goalId = first(payload.goalId, payload.goal_id, type === 'SET_SELECTED_GOAL' ? payload.id : '');
  let client = first(
    payload.client, payload.clientId, payload.userId, payload.user_id,
    payload.targetUserId, payload.target_user_id,
  );
  if (!client) {
    const { pathname, search } = currentAgentLocation();
    const hereKey = pathname ? screenForPath(pathname) : null;
    const here = hereKey ? paramsFor(hereKey, pathname, search) || {} : {};
    client = first(here.client, hereKey === 'client360' ? here.id : '');
  }
  if (!client) client = first(agentRuntime().store.getState()?.ui?.client);
  if (!goalId || !client) {
    debug('agent → goal selection ignored (goal or client unknown)', { type, goalId, client });
    return false;
  }
  const res = await agentNavigate({ screen: 'goal', params: { goalId, client }, silent: true });
  return res.ok;
}

/**
 * The mutual-fund screen an envelope hands out, or ''.
 *
 * `screen_mutual_funds` stores its result under a `url_id` and the agent sends
 * that id in `data`, beside the navigation rather than in it. Without it the
 * research screen would keep showing its own category list, not what the
 * agent screened. A stock or model screen's id is not a fund screen, so a
 * payload suggesting those, or naming another screen type, is skipped.
 */
function fundScreenId(commands) {
  for (const { payload } of commands) {
    if (!isObject(payload)) continue;
    const id = screenHandle(payload);
    if (!id) continue;
    /* A payload that CARRIES stocks or models is one of those screens, whatever
       it calls itself. Length-checked: the fund screener sends these keys empty
       beside its own list, and an empty array is truthy. */
    if (payload.suggestedStocks?.length || payload.suggestedAssetModels?.length) continue;
    if (!isFundScreen(payload)) continue;
    return id;
  }
  return '';
}

/* ── envelope ───────────────────────────────────────────────────────────── */

/** Whether a form update carries any field its form declares (null and blank count: they clear it). */
function namesFormField(type, payload) {
  if (!isObject(payload)) return false;
  const fields = AGENT_FORMS[type]?.fields || [];
  return fields.some((field) => payload[field] !== undefined);
}

/**
 * Where an envelope's form updates land: the screen open once its navigation
 * has run, and the params of that URL. Sent as `meta` on every form update, so
 * a form that holds one subject's values (`kycForm`, one client's KYC record)
 * can record whose they are; the other forms never read it.
 */
function formUpdateMeta() {
  const { pathname, search } = currentAgentLocation();
  const screen = pathname ? screenForPath(pathname) : null;
  return { screen, params: screen ? paramsFor(screen, pathname, search) || {} : {} };
}

/** Which phase a command belongs to; see `executeEnvelope`. */
function phaseOf(type) {
  if (Object.prototype.hasOwnProperty.call(AGENT_FORMS, type)) return 'form';
  if (AGENT_SIGNALS.includes(type)) return 'signal';
  if (RETAIL_SIGNALS.includes(type)) return 'retail';
  return 'legacy';
}

/**
 * The retail form an envelope fills, or null.
 *
 * `REQUEST_RUN_ANALYSIS` is the one signal the retail prompts and the desk
 * share. The recommendation and goal-planning agents send it beside their own
 * form (`UPDATE_RECOMMENDATION_DATA`, `UPDATE_<GOAL>_DATA`) to run THEIR
 * analysis; on the desk it would run whatever the open screen runs — a queued
 * book or fund review nobody asked for. An `UPDATE_*` the catalogue does not
 * declare, and that is not a link card, marks the envelope as one of those.
 */
function retailFormIn(commands) {
  const found = commands.find(({ type }) => type.startsWith('UPDATE_')
    && phaseOf(type) === 'legacy' && !LINK_CARD_TYPES.includes(type));
  return found ? found.type : null;
}

/**
 * Execute a normalized envelope (`normalizeEnvelope(payload)`); a raw payload is
 * normalized first so devtools and tests can pass either.
 *
 * Order, whatever order the agent wrote the commands in:
 * 1. navigate (awaited, ≤ 2 s) — `data.navigation` or `actions.navigate`;
 * 2. `UPDATE_*` form updates, dispatched to the `agent` slice with
 *    `meta: { screen, params }` for the screen they landed on;
 * 3. `REQUEST_*` signals, each started once, in order;
 * 4. legacy commands (links cards, goal selection) and one refusal for any
 *    retail signal — which includes a `REQUEST_RUN_ANALYSIS` sent beside a
 *    retail form (`retailFormIn`): it is not started.
 * A signal therefore always sees the screen it was sent with and the fields
 * sent beside it — "fill, navigate, run" in one envelope works however the
 * model ordered its list.
 *
 * Signals are started on a chain of their own and not awaited here: a page may
 * hold one on a two-minute confirmation card, and the provider settles the
 * turn when this resolves. When the envelope's navigate failed (and said so),
 * its signals are not started — they were meant for a screen the user is not
 * on, and the navigate report already tells the agent why.
 *
 * Resolves `{ navigated, commands }`: whether a navigation landed, and how many
 * commands were handled (applied, started, or refused with a report).
 */
export async function executeEnvelope(normalized) {
  const outcome = { navigated: false, commands: 0 };
  if (normalized == null || typeof normalized !== 'object') return outcome;
  const env = Array.isArray(normalized.commands) ? normalized : normalizeEnvelope(normalized);
  if (!env) return outcome;
  const runtime = agentRuntime();

  const commands = (Array.isArray(env.commands) ? env.commands : [])
    .map((command) => ({ type: str(command?.type), payload: command?.payload }))
    .filter((command) => command.type);
  const inPhase = (phase) => commands.filter((command) => phaseOf(command.type) === phase);

  /* 1 — navigation. `actions.navigate` that names no PMS screen is dropped
     silently: the handoff can carry React Native screen names meant for the
     phone app. Not when signals came with it, though: they were meant for the
     screen it names, and started on the screen that is open they would run
     something nobody asked for — so that navigate fails, and says so, like
     `data.navigation`. A name that does resolve is held to the same rules as
     `data.navigation`, whose failures are reported. */
  let navigationFailed = false;
  const nav = isObject(env.navigate) ? env.navigate : null;
  const screenId = fundScreenId(commands);
  if (nav) {
    try {
      if (nav.source === 'actions' && !resolveScreen(nav.screen)) {
        if (inPhase('signal').length) {
          navigationFailed = true;
          runtime.report(formatApiResult({
            op: 'navigate', ok: false, error: `unknown screen ${JSON.stringify(str(nav.screen))}`,
          }));
        } else {
          debug('agent → actions.navigate ignored (not a PMS screen)', nav);
        }
      } else {
        let params = isObject(nav.params) ? nav.params : {};
        let clear = [];
        if (resolveScreen(nav.screen) === 'research' && !first(params.url_id)) {
          /* The screen the agent just built, shown in place of the category
             list; a new search or category without one means that screen no
             longer describes what the agent wants shown. */
          if (screenId) params = { ...params, url_id: screenId };
          else if (first(params.q) || first(params.category)) clear = ['url_id'];
        }
        const res = await agentNavigate({ screen: nav.screen, params, clear });
        outcome.navigated = Boolean(res.ok);
        navigationFailed = !res.ok;
        if (res.ok) keepAssistantInView();
      }
    } catch (err) {
      navigationFailed = true;
      runtime.report(formatApiResult({ op: 'navigate', ok: false, error: `client error — ${err?.message || err}` }));
    }
  } else if (screenId) {
    /* A screen built with no navigation, while the reader is already on the
       research screen: show it there. Anywhere else the link card offers it. */
    const { pathname } = currentAgentLocation();
    if (pathname && screenForPath(pathname) === 'research') {
      const res = await agentNavigate({ screen: 'research', params: { url_id: screenId }, silent: true });
      if (res.ok) outcome.navigated = true;
    }
  }

  const guard = async (type, fn) => {
    try {
      await fn();
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error(`[agent] ${type} failed`, err);
      runtime.report(formatApiResult({ op: type, ok: false, error: `client error — ${err?.message || String(err)}` }));
    }
  };

  /* 2 — form updates. An update counts when it names at least one of its
     form's fields, even as null or blank text: that is how a field is cleared
     back to its default, and the slice does the clearing. The empty-payload
     rule of the legacy commands below would throw such a clear away. Each
     carries the screen it landed on as `meta`. */
  for (const { type, payload } of inPhase('form')) {
    // eslint-disable-next-line no-await-in-loop -- commands apply in order.
    await guard(type, () => {
      if (!DECLARED_ACTIONS.has(type)) {
        debug('agent → form update not declared in the catalogue, ignored', { type });
        return;
      }
      if (!namesFormField(type, payload)) {
        debug('agent → form update names none of its fields, ignored', { type });
        return;
      }
      outcome.commands += 1;
      runtime.store.dispatch({ type, payload, meta: formUpdateMeta() });
    });
  }

  /* 3 — signals, once each, on their own chain. */
  const retailForm = retailFormIn(commands);
  const started = new Set();
  let signalChain = Promise.resolve();
  let refusedRun = false;
  for (const { type } of inPhase('signal')) {
    if (started.has(type)) continue;
    started.add(type);
    outcome.commands += 1;
    if (type === RUN_ANALYSIS && retailForm) {
      debug('agent → REQUEST_RUN_ANALYSIS beside a retail form, not started', { retailForm });
      refusedRun = true;
      continue;
    }
    if (navigationFailed) {
      debug('agent → signal not started (the navigate before it failed)', { type });
      continue;
    }
    signalChain = signalChain
      .then(() => startAgentSignal(type))
      .catch((err) => {
        runtime.report(formatApiResult({ op: type, ok: false, error: `client error — ${err?.message || err}` }));
      });
  }

  /* 4 — legacy commands, then one refusal for any retail signal. */
  for (const { type, payload: raw } of inPhase('legacy')) {
    const payload = isObject(raw) ? raw : {};
    // eslint-disable-next-line no-await-in-loop -- commands apply in order.
    await guard(type, async () => {
      if ((type.startsWith('UPDATE_') || type.startsWith('SET_')) && isEmptyPayload(raw)) {
        debug('agent → empty payload ignored', { type });
        return;
      }
      switch (type) {
        case 'UPDATE_SUGGESTION_DATA':
          outcome.commands += 1;
          suggestionLinks(payload);
          break;
        case 'UPDATE_SCREEN_BUILDER_RESULT':
          outcome.commands += 1;
          screenBuilderLink(payload);
          break;
        case 'SET_SELECTED_GOAL':
        case 'SET_AUTO_SELECT_GOAL_ID':
          outcome.commands += 1;
          if (await selectGoal(type, payload)) outcome.navigated = true;
          break;
        default:
          debug('agent → command not in the PMS vocabulary, ignored', { type, payload });
      }
    });
  }

  /* One refusal per envelope: a retail signal's, else the run that came with a
     retail form — neither when the envelope's navigate already failed and
     said so, since that report is the envelope's one answer. */
  const retail = inPhase('retail');
  if (retail.length) {
    outcome.commands += retail.length;
    if (!navigationFailed) {
      runtime.report(formatApiResult({ op: retail[0].type.toLowerCase(), ok: false, error: RETAIL_REFUSAL }));
    }
  } else if (refusedRun && !navigationFailed) {
    runtime.report(formatApiResult({
      op: RUN_ANALYSIS, ok: false, error: `${RETAIL_REFUSAL} — ${retailForm} is not a PMS form`,
    }));
  }

  return outcome;
}
